import React, { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import api from '../../services/api';
import GenericFormDrawer from '../../components/GenericFormDrawer';

// Single source of truth for the reference-check form. Used by both the
// References tab (full CRUD list) and the application Overview tab's
// reference-stage card, so the field schema isn't duplicated across call sites.
export const REFERENCE_STATUSES = ['requested', 'completed', 'unreachable', 'declined'];

export default function ReferenceCheckFormDrawer({ open, applicationId, reference, onClose, onSaved }) {
  const { t } = useTranslation();

  const statusOptions = useMemo(() => REFERENCE_STATUSES.map((s) => ({
    id: s, name: t(`ats.referenceCheck.status.${s}`),
  })), [t]);

  const schema = useMemo(() => [
    { name: 'refereeName', label: t('ats.referenceCheck.field.refereeName'), type: 'text', grid: 6, required: true },
    { name: 'relationship', label: t('ats.referenceCheck.field.relationship'), type: 'text', grid: 6 },
    { name: 'refereeTitle', label: t('ats.referenceCheck.field.refereeTitle'), type: 'text', grid: 6 },
    { name: 'refereeCompany', label: t('ats.referenceCheck.field.refereeCompany'), type: 'text', grid: 6 },
    { name: 'refereePhone', label: t('ats.referenceCheck.field.refereePhone'), type: 'text', grid: 6, required: true },
    { name: 'refereeEmail', label: t('ats.referenceCheck.field.refereeEmail'), type: 'email', grid: 6, required: true },
    {
      name: 'status', label: t('ats.referenceCheck.field.status'), type: 'select', grid: 6,
      options: statusOptions, idKey: 'id', labelKey: 'name', required: true,
    },
    { name: 'rating', label: t('ats.referenceCheck.field.rating'), type: 'number', grid: 6, min: 1, max: 5, step: 1 },
    { name: 'checkedAt', label: t('ats.referenceCheck.field.checkedAt'), type: 'date', grid: 6 },
    { name: 'feedback', label: t('ats.referenceCheck.field.feedback'), type: 'textarea', grid: 12, rows: 4 },
  ], [t, statusOptions]);

  const handleSave = useCallback(async (formData) => {
    try {
      const payload = { ...formData, applicationId };
      if (reference) {
        await api.put(`/v1/ats-reference-checks/${reference.id}`, payload);
      } else {
        await api.post('/v1/ats-reference-checks', payload);
      }
      if (onClose) onClose();
      if (onSaved) onSaved();
      return true;
    } catch {
      return false;
    }
  }, [applicationId, reference, onClose, onSaved]);

  return (
    <GenericFormDrawer
      open={open}
      onClose={onClose}
      title={reference ? t('ats.referenceCheck.edit') : t('ats.referenceCheck.new')}
      schema={schema}
      initialValues={reference || { status: 'requested' }}
      onSave={handleSave}
    />
  );
}
